export interface BuiltInTemplate {
  id: string;
  name: string;
  description: string;
  subject: string;
  content: string;
  category: "founders" | "engineering" | "product";
}

export const builtInTemplates: BuiltInTemplate[] = [
  // ============================================
  // FOUNDERS TEMPLATES
  // ============================================
  {
    id: "investor-update",
    name: "Investor Update",
    description: "Monthly or quarterly update to keep investors informed on progress",
    subject: "[Company Name] - [Month] [Year] Investor Update",
    content: `<h2>Monthly Investor Update</h2>
<p>Dear Investors,</p>
<p>I hope this update finds you well. Here's our progress report for <strong>[Month Year]</strong>.</p>

<h3>📊 Key Metrics</h3>
<table>
<tr><td><strong>MRR:</strong></td><td>$[XXX,XXX] ([+/-XX%] MoM)</td></tr>
<tr><td><strong>ARR:</strong></td><td>$[X.XM]</td></tr>
<tr><td><strong>Active Users:</strong></td><td>[X,XXX] ([+/-XX%] MoM)</td></tr>
<tr><td><strong>Burn Rate:</strong></td><td>$[XXX,XXX]/month</td></tr>
<tr><td><strong>Runway:</strong></td><td>[XX] months</td></tr>
</table>

<h3>🎯 Highlights</h3>
<ul>
<li><strong>Revenue:</strong> [Describe revenue milestone or growth]</li>
<li><strong>Product:</strong> [Key product achievement]</li>
<li><strong>Team:</strong> [Hiring updates or team wins]</li>
<li><strong>Customers:</strong> [Notable customer wins or retention metrics]</li>
</ul>

<h3>🚀 Product Updates</h3>
<p>[Describe major features shipped, product improvements, or roadmap progress]</p>

<h3>💼 Business Development</h3>
<p>[Partnership updates, sales pipeline, enterprise deals in progress]</p>

<h3>👥 Team & Culture</h3>
<p>[New hires, organizational changes, culture initiatives]</p>

<h3>⚠️ Challenges & Learnings</h3>
<p>[Be transparent about obstacles and what you're learning]</p>

<h3>🔮 Looking Ahead</h3>
<p><strong>Next Month Goals:</strong></p>
<ol>
<li>[Goal 1 with target metric]</li>
<li>[Goal 2 with target metric]</li>
<li>[Goal 3 with target metric]</li>
</ol>

<h3>🙏 Asks</h3>
<p>[Specific ways investors can help - intros, advice, resources]</p>

<p>Thank you for your continued support. Happy to jump on a call if you'd like to discuss anything in detail.</p>

<p>Best regards,<br/>
<strong>[Your Name]</strong><br/>
CEO & Co-founder, [Company Name]</p>`,
    category: "founders",
  },
  {
    id: "board-update",
    name: "Board Update",
    description: "Pre or post-board meeting summary for board members",
    subject: "[Company Name] Board Update - [Quarter] [Year]",
    content: `<h2>Board Update - [Quarter] [Year]</h2>
<p>Dear Board Members,</p>
<p>Please find below our quarterly update in preparation for our upcoming board meeting on <strong>[Date]</strong>.</p>

<h3>📋 Executive Summary</h3>
<p>[2-3 sentence summary of the quarter's performance and key themes]</p>

<h3>📊 Financial Performance</h3>
<table>
<tr><th>Metric</th><th>Target</th><th>Actual</th><th>Variance</th></tr>
<tr><td>Revenue</td><td>$[XXX]</td><td>$[XXX]</td><td>[+/-X%]</td></tr>
<tr><td>Gross Margin</td><td>[XX%]</td><td>[XX%]</td><td>[+/-X%]</td></tr>
<tr><td>Net Burn</td><td>$[XXX]</td><td>$[XXX]</td><td>[+/-X%]</td></tr>
<tr><td>Cash Position</td><td>$[X.XM]</td><td>$[X.XM]</td><td>—</td></tr>
</table>

<h3>🎯 OKR Progress</h3>
<p><strong>Objective 1:</strong> [Objective Name]</p>
<ul>
<li>KR1: [Key Result] - [X%] complete</li>
<li>KR2: [Key Result] - [X%] complete</li>
</ul>

<p><strong>Objective 2:</strong> [Objective Name]</p>
<ul>
<li>KR1: [Key Result] - [X%] complete</li>
<li>KR2: [Key Result] - [X%] complete</li>
</ul>

<h3>🏆 Key Wins</h3>
<ol>
<li>[Major achievement with context and impact]</li>
<li>[Major achievement with context and impact]</li>
<li>[Major achievement with context and impact]</li>
</ol>

<h3>⚠️ Risks & Mitigation</h3>
<table>
<tr><th>Risk</th><th>Impact</th><th>Likelihood</th><th>Mitigation</th></tr>
<tr><td>[Risk 1]</td><td>High/Med/Low</td><td>High/Med/Low</td><td>[Action]</td></tr>
<tr><td>[Risk 2]</td><td>High/Med/Low</td><td>High/Med/Low</td><td>[Action]</td></tr>
</table>

<h3>🗳️ Items for Board Approval</h3>
<ol>
<li>[Item requiring approval with recommendation]</li>
<li>[Item requiring approval with recommendation]</li>
</ol>

<h3>📅 Agenda for Board Meeting</h3>
<ol>
<li>Financial Review (20 min)</li>
<li>Product & Roadmap Update (15 min)</li>
<li>Go-to-Market Strategy (15 min)</li>
<li>Organizational Update (10 min)</li>
<li>Strategic Discussion: [Topic] (30 min)</li>
<li>Q&A and Wrap-up (15 min)</li>
</ol>

<p>Looking forward to our discussion.</p>

<p>Best,<br/>
<strong>[Your Name]</strong></p>`,
    category: "founders",
  },
  {
    id: "fundraising-update",
    name: "Fundraising Update",
    description: "Update to existing investors during an active fundraise",
    subject: "[Company Name] - Fundraising Update [Date]",
    content: `<h2>Fundraising Update</h2>
<p>Dear Investors,</p>
<p>I wanted to share a quick update on our Series [X] fundraising progress.</p>

<h3>💰 Round Status</h3>
<table>
<tr><td><strong>Target Raise:</strong></td><td>$[X]M</td></tr>
<tr><td><strong>Committed:</strong></td><td>$[X]M ([XX%] of target)</td></tr>
<tr><td><strong>Soft Circled:</strong></td><td>$[X]M</td></tr>
<tr><td><strong>Lead Investor:</strong></td><td>[Confirmed/In discussions with X]</td></tr>
<tr><td><strong>Target Close:</strong></td><td>[Date]</td></tr>
</table>

<h3>📈 Investor Pipeline</h3>
<ul>
<li><strong>In Term Sheet:</strong> [X] firms</li>
<li><strong>Partner Meeting Scheduled:</strong> [X] firms</li>
<li><strong>First Meeting Completed:</strong> [X] firms</li>
<li><strong>Passed:</strong> [X] firms (key feedback: [summary])</li>
</ul>

<h3>🎯 Recent Traction (Since Last Update)</h3>
<ul>
<li>[New metric or milestone]</li>
<li>[Customer win or partnership]</li>
<li>[Product achievement]</li>
</ul>

<h3>🙏 How You Can Help</h3>
<p>Warm intros to the following would be incredibly valuable:</p>
<ul>
<li>[VC Firm 1] - [Partner Name]</li>
<li>[VC Firm 2] - [Partner Name]</li>
<li>[VC Firm 3] - [Partner Name]</li>
</ul>

<h3>📅 Timeline</h3>
<ul>
<li><strong>This Week:</strong> [X] partner meetings</li>
<li><strong>Next Week:</strong> [Expected term sheets / decisions]</li>
<li><strong>Target Close:</strong> [Date]</li>
</ul>

<p>I'll send another update in [X] days. Please let me know if you have any questions or can make introductions.</p>

<p>Thank you for your support!</p>

<p>Best,<br/>
<strong>[Your Name]</strong></p>`,
    category: "founders",
  },
  {
    id: "company-all-hands",
    name: "All-Hands Meeting Recap",
    description: "Summary of company all-hands for team members who missed it",
    subject: "[Company Name] All-Hands Recap - [Date]",
    content: `<h2>All-Hands Meeting Recap</h2>
<p>Hi Team,</p>
<p>For those who couldn't attend today's all-hands, here's a summary of what we covered.</p>

<h3>📊 Company Performance</h3>
<ul>
<li><strong>Revenue:</strong> [Current status vs. target]</li>
<li><strong>Customer Growth:</strong> [Key metrics]</li>
<li><strong>Team Size:</strong> [Current headcount]</li>
</ul>

<h3>🎯 Q[X] Goals Progress</h3>
<ol>
<li><strong>[Goal 1]:</strong> [Status - On Track/At Risk/Achieved]</li>
<li><strong>[Goal 2]:</strong> [Status]</li>
<li><strong>[Goal 3]:</strong> [Status]</li>
</ol>

<h3>🏆 Shoutouts & Wins</h3>
<ul>
<li>🌟 [Team/Person] for [Achievement]</li>
<li>🌟 [Team/Person] for [Achievement]</li>
<li>🌟 [Team/Person] for [Achievement]</li>
</ul>

<h3>📣 Key Announcements</h3>
<ol>
<li><strong>[Announcement 1]:</strong> [Details and what it means for the team]</li>
<li><strong>[Announcement 2]:</strong> [Details]</li>
</ol>

<h3>🔮 Looking Ahead</h3>
<p>[Preview of upcoming initiatives, events, or focus areas]</p>

<h3>❓ Q&A Highlights</h3>
<p><strong>Q:</strong> [Question asked]<br/>
<strong>A:</strong> [Answer provided]</p>

<p><strong>Q:</strong> [Question asked]<br/>
<strong>A:</strong> [Answer provided]</p>

<h3>📹 Recording</h3>
<p>Full recording available here: [Link]</p>

<p>Questions? Drop them in #all-hands-questions on Slack.</p>

<p>— [Your Name]</p>`,
    category: "founders",
  },

  // ============================================
  // ENGINEERING TEMPLATES
  // ============================================
  {
    id: "engineering-team-update",
    name: "Engineering Team Update",
    description: "Weekly or bi-weekly update to leadership on engineering progress",
    subject: "Engineering Update - Week of [Date]",
    content: `<h2>Engineering Weekly Update</h2>
<p><strong>Week of [Date]</strong></p>

<h3>📊 Sprint Metrics</h3>
<table>
<tr><td><strong>Sprint Goal Progress:</strong></td><td>[XX%] complete</td></tr>
<tr><td><strong>Story Points Completed:</strong></td><td>[XX] / [XX] planned</td></tr>
<tr><td><strong>Bugs Fixed:</strong></td><td>[XX]</td></tr>
<tr><td><strong>Tech Debt Items:</strong></td><td>[XX] resolved</td></tr>
</table>

<h3>✅ Completed This Week</h3>
<ul>
<li><strong>[Feature/Project 1]:</strong> [Brief description and impact]</li>
<li><strong>[Feature/Project 2]:</strong> [Brief description and impact]</li>
<li><strong>[Bug Fix/Improvement]:</strong> [What was fixed and why it matters]</li>
</ul>

<h3>🚧 In Progress</h3>
<ul>
<li><strong>[Feature/Project]:</strong> [Status, expected completion, any blockers]</li>
<li><strong>[Feature/Project]:</strong> [Status, expected completion, any blockers]</li>
</ul>

<h3>📅 Upcoming Next Week</h3>
<ol>
<li>[Priority item with owner]</li>
<li>[Priority item with owner]</li>
<li>[Priority item with owner]</li>
</ol>

<h3>⚠️ Blockers & Risks</h3>
<ul>
<li><strong>[Blocker]:</strong> [Description and what's needed to unblock]</li>
<li><strong>[Risk]:</strong> [Description and mitigation plan]</li>
</ul>

<h3>🔧 Technical Debt & Infrastructure</h3>
<ul>
<li>[Update on any infrastructure changes or tech debt work]</li>
</ul>

<h3>👥 Team Updates</h3>
<ul>
<li>[PTO, new hires, team changes]</li>
</ul>

<h3>📈 Key Metrics</h3>
<ul>
<li><strong>Uptime:</strong> [XX.XX%]</li>
<li><strong>P95 Latency:</strong> [XXms]</li>
<li><strong>Error Rate:</strong> [X.XX%]</li>
</ul>

<p>Questions or concerns? Let's discuss in our next standup or Slack me directly.</p>

<p>— [Your Name], Engineering Lead</p>`,
    category: "engineering",
  },
  {
    id: "incident-postmortem",
    name: "Incident Postmortem",
    description: "Post-incident summary for stakeholders after an outage or issue",
    subject: "[P1/P2/P3] Incident Postmortem - [Brief Description] - [Date]",
    content: `<h2>Incident Postmortem</h2>
<p><strong>Incident:</strong> [Brief description]<br/>
<strong>Severity:</strong> [P1/P2/P3]<br/>
<strong>Date:</strong> [Date]<br/>
<strong>Duration:</strong> [X hours Y minutes]<br/>
<strong>Author:</strong> [Your Name]</p>

<h3>📋 Executive Summary</h3>
<p>[2-3 sentence summary of what happened, impact, and resolution]</p>

<h3>📊 Impact</h3>
<table>
<tr><td><strong>Users Affected:</strong></td><td>[Number or percentage]</td></tr>
<tr><td><strong>Revenue Impact:</strong></td><td>$[Estimated amount]</td></tr>
<tr><td><strong>Duration:</strong></td><td>[Time from detection to resolution]</td></tr>
<tr><td><strong>Services Affected:</strong></td><td>[List of services]</td></tr>
</table>

<h3>⏱️ Timeline (All times in [Timezone])</h3>
<table>
<tr><th>Time</th><th>Event</th></tr>
<tr><td>[HH:MM]</td><td><strong>Trigger:</strong> [What initiated the incident]</td></tr>
<tr><td>[HH:MM]</td><td><strong>Detection:</strong> [How it was detected - alert, customer report, etc.]</td></tr>
<tr><td>[HH:MM]</td><td><strong>Response:</strong> [First responder engaged]</td></tr>
<tr><td>[HH:MM]</td><td><strong>Escalation:</strong> [Additional team members brought in]</td></tr>
<tr><td>[HH:MM]</td><td><strong>Mitigation:</strong> [First mitigation applied]</td></tr>
<tr><td>[HH:MM]</td><td><strong>Resolution:</strong> [Full resolution confirmed]</td></tr>
</table>

<h3>🔍 Root Cause</h3>
<p>[Detailed technical explanation of what caused the incident]</p>

<h3>🔧 Resolution</h3>
<p>[What was done to resolve the incident]</p>

<h3>📚 Lessons Learned</h3>
<p><strong>What went well:</strong></p>
<ul>
<li>[Positive observation]</li>
<li>[Positive observation]</li>
</ul>

<p><strong>What could be improved:</strong></p>
<ul>
<li>[Area for improvement]</li>
<li>[Area for improvement]</li>
</ul>

<h3>✅ Action Items</h3>
<table>
<tr><th>Action</th><th>Owner</th><th>Priority</th><th>Due Date</th></tr>
<tr><td>[Preventive measure 1]</td><td>[Name]</td><td>P1</td><td>[Date]</td></tr>
<tr><td>[Preventive measure 2]</td><td>[Name]</td><td>P2</td><td>[Date]</td></tr>
<tr><td>[Process improvement]</td><td>[Name]</td><td>P2</td><td>[Date]</td></tr>
</table>

<h3>📎 Supporting Documents</h3>
<ul>
<li>[Link to incident channel/thread]</li>
<li>[Link to relevant dashboards]</li>
<li>[Link to related tickets]</li>
</ul>`,
    category: "engineering",
  },
  {
    id: "release-notes",
    name: "Release Notes",
    description: "Internal release notes for engineering and stakeholders",
    subject: "Release Notes - v[X.Y.Z] - [Date]",
    content: `<h2>Release Notes - v[X.Y.Z]</h2>
<p><strong>Release Date:</strong> [Date]<br/>
<strong>Release Manager:</strong> [Name]</p>

<h3>📋 Release Summary</h3>
<p>[Brief overview of what's included in this release]</p>

<h3>🚀 New Features</h3>
<ul>
<li><strong>[Feature Name]:</strong> [Description and user benefit] <em>(Ticket: [JIRA-XXX])</em></li>
<li><strong>[Feature Name]:</strong> [Description and user benefit] <em>(Ticket: [JIRA-XXX])</em></li>
</ul>

<h3>🔧 Improvements</h3>
<ul>
<li>[Improvement description] <em>(Ticket: [JIRA-XXX])</em></li>
<li>[Improvement description] <em>(Ticket: [JIRA-XXX])</em></li>
</ul>

<h3>🐛 Bug Fixes</h3>
<ul>
<li><strong>[Bug Title]:</strong> [What was fixed] <em>(Ticket: [JIRA-XXX])</em></li>
<li><strong>[Bug Title]:</strong> [What was fixed] <em>(Ticket: [JIRA-XXX])</em></li>
</ul>

<h3>⚠️ Breaking Changes</h3>
<ul>
<li><strong>[Change]:</strong> [Migration steps or impact]</li>
</ul>

<h3>📊 Database Migrations</h3>
<ul>
<li>[Migration description and any required actions]</li>
</ul>

<h3>🔒 Security Updates</h3>
<ul>
<li>[Security patch or update]</li>
</ul>

<h3>📝 Configuration Changes</h3>
<ul>
<li>[New env variable or config change]</li>
</ul>

<h3>📦 Dependencies Updated</h3>
<ul>
<li>[Package]: [old version] → [new version]</li>
</ul>

<h3>🧪 Testing Notes</h3>
<p>[Any specific areas that need extra testing or known limitations]</p>

<h3>🔙 Rollback Plan</h3>
<p>[Steps to rollback if issues are discovered]</p>`,
    category: "engineering",
  },
  {
    id: "architecture-decision",
    name: "Architecture Decision Record",
    description: "Document technical decisions and their rationale",
    subject: "ADR: [Decision Title] - [Date]",
    content: `<h2>Architecture Decision Record</h2>
<p><strong>ADR #:</strong> [XXX]<br/>
<strong>Title:</strong> [Decision Title]<br/>
<strong>Status:</strong> [Proposed/Accepted/Deprecated/Superseded]<br/>
<strong>Date:</strong> [Date]<br/>
<strong>Author:</strong> [Name]</p>

<h3>📋 Context</h3>
<p>[Describe the issue motivating this decision. What is the problem we're trying to solve? What constraints do we have?]</p>

<h3>🎯 Decision</h3>
<p>[Clearly state the decision that was made]</p>

<h3>🔄 Alternatives Considered</h3>

<p><strong>Option 1: [Name]</strong></p>
<ul>
<li>Pros: [List advantages]</li>
<li>Cons: [List disadvantages]</li>
</ul>

<p><strong>Option 2: [Name]</strong></p>
<ul>
<li>Pros: [List advantages]</li>
<li>Cons: [List disadvantages]</li>
</ul>

<p><strong>Option 3: [Name] (Selected)</strong></p>
<ul>
<li>Pros: [List advantages]</li>
<li>Cons: [List disadvantages]</li>
</ul>

<h3>📊 Consequences</h3>
<p><strong>Positive:</strong></p>
<ul>
<li>[Positive outcome]</li>
<li>[Positive outcome]</li>
</ul>

<p><strong>Negative:</strong></p>
<ul>
<li>[Negative outcome or trade-off]</li>
<li>[Negative outcome or trade-off]</li>
</ul>

<p><strong>Neutral:</strong></p>
<ul>
<li>[Neutral impact]</li>
</ul>

<h3>📅 Implementation Plan</h3>
<ol>
<li>[Step 1 with timeline]</li>
<li>[Step 2 with timeline]</li>
<li>[Step 3 with timeline]</li>
</ol>

<h3>📎 References</h3>
<ul>
<li>[Link to relevant documentation]</li>
<li>[Link to related ADRs]</li>
<li>[Link to discussion thread]</li>
</ul>`,
    category: "engineering",
  },

  // ============================================
  // PRODUCT TEMPLATES
  // ============================================
  {
    id: "product-update",
    name: "Product Update",
    description: "Regular update to stakeholders on product progress and roadmap",
    subject: "Product Update - [Sprint/Month] [Year]",
    content: `<h2>Product Update</h2>
<p><strong>Period:</strong> [Sprint X / Month Year]<br/>
<strong>Author:</strong> [Your Name], Product</p>

<h3>📊 Key Metrics</h3>
<table>
<tr><td><strong>DAU:</strong></td><td>[X,XXX] ([+/-XX%] vs last period)</td></tr>
<tr><td><strong>Feature Adoption:</strong></td><td>[Key feature] at [XX%] adoption</td></tr>
<tr><td><strong>NPS:</strong></td><td>[Score] ([+/-X] vs last period)</td></tr>
<tr><td><strong>Support Tickets:</strong></td><td>[XXX] ([+/-XX%] vs last period)</td></tr>
</table>

<h3>🚀 Shipped This Period</h3>
<ul>
<li><strong>[Feature 1]:</strong> [Description, user benefit, and initial metrics]</li>
<li><strong>[Feature 2]:</strong> [Description, user benefit, and initial metrics]</li>
<li><strong>[Improvement]:</strong> [What was improved and impact]</li>
</ul>

<h3>📈 Feature Performance</h3>
<p><strong>[Recently Launched Feature]:</strong></p>
<ul>
<li>Adoption: [XX%] of target users</li>
<li>Engagement: [Metric]</li>
<li>Feedback: [Summary of user feedback]</li>
<li>Next iteration: [Planned improvements]</li>
</ul>

<h3>🎯 Roadmap Progress</h3>
<table>
<tr><th>Initiative</th><th>Status</th><th>ETA</th></tr>
<tr><td>[Initiative 1]</td><td>🟢 On Track</td><td>[Date]</td></tr>
<tr><td>[Initiative 2]</td><td>🟡 At Risk</td><td>[Date]</td></tr>
<tr><td>[Initiative 3]</td><td>🔵 Planning</td><td>[Date]</td></tr>
</table>

<h3>🔬 User Research Insights</h3>
<ul>
<li><strong>Finding 1:</strong> [Insight and implication]</li>
<li><strong>Finding 2:</strong> [Insight and implication]</li>
</ul>

<h3>🐛 Top Customer Issues</h3>
<ol>
<li>[Issue] - [Status: Fixed/In Progress/Backlogged]</li>
<li>[Issue] - [Status]</li>
<li>[Issue] - [Status]</li>
</ol>

<h3>📅 Coming Next</h3>
<ul>
<li><strong>Next Sprint:</strong> [Focus areas]</li>
<li><strong>Next Month:</strong> [Planned releases]</li>
</ul>

<h3>🙏 Cross-Functional Asks</h3>
<ul>
<li><strong>Engineering:</strong> [Any requests]</li>
<li><strong>Design:</strong> [Any requests]</li>
<li><strong>Sales:</strong> [Any requests]</li>
</ul>

<p>Questions? Let's discuss in #product-updates or our next sync.</p>

<p>— [Your Name]</p>`,
    category: "product",
  },
  {
    id: "feature-launch",
    name: "Feature Launch Announcement",
    description: "Internal announcement for a new feature or product launch",
    subject: "🚀 Launching: [Feature Name]",
    content: `<h2>🚀 Feature Launch: [Feature Name]</h2>
<p>Hi Team!</p>
<p>I'm excited to announce that <strong>[Feature Name]</strong> is now live!</p>

<h3>🎯 What Is It?</h3>
<p>[Clear, concise explanation of the feature and what problem it solves]</p>

<h3>👥 Who Is It For?</h3>
<ul>
<li><strong>Primary users:</strong> [User segment]</li>
<li><strong>Use case:</strong> [When/why they would use this]</li>
</ul>

<h3>✨ Key Capabilities</h3>
<ol>
<li><strong>[Capability 1]:</strong> [Description]</li>
<li><strong>[Capability 2]:</strong> [Description]</li>
<li><strong>[Capability 3]:</strong> [Description]</li>
</ol>

<h3>📸 Screenshots / Demo</h3>
<p>[Include screenshots or link to demo video]</p>

<h3>🎓 How to Use It</h3>
<ol>
<li>[Step 1]</li>
<li>[Step 2]</li>
<li>[Step 3]</li>
</ol>

<h3>💬 Talking Points for Customer-Facing Teams</h3>
<ul>
<li>[Key benefit to highlight]</li>
<li>[Comparison to alternatives]</li>
<li>[Pricing/availability info]</li>
</ul>

<h3>⚠️ Known Limitations</h3>
<ul>
<li>[Limitation 1 and workaround if any]</li>
<li>[Limitation 2]</li>
</ul>

<h3>📊 Success Metrics</h3>
<p>We'll measure success by:</p>
<ul>
<li>[Metric 1]: Target of [X]</li>
<li>[Metric 2]: Target of [X]</li>
</ul>

<h3>📚 Resources</h3>
<ul>
<li>📄 Documentation: [Link]</li>
<li>🎥 Demo Video: [Link]</li>
<li>❓ FAQ: [Link]</li>
</ul>

<h3>🙏 Acknowledgments</h3>
<p>Huge thanks to everyone who made this happen:</p>
<ul>
<li>Engineering: [Names]</li>
<li>Design: [Names]</li>
<li>QA: [Names]</li>
</ul>

<p>Questions? Reach out in #[feature-channel] or ping me directly!</p>

<p>— [Your Name]</p>`,
    category: "product",
  },
  {
    id: "product-spec",
    name: "Product Requirements Document",
    description: "PRD template for new features or initiatives",
    subject: "PRD: [Feature Name]",
    content: `<h2>Product Requirements Document</h2>
<p><strong>Feature:</strong> [Feature Name]<br/>
<strong>Author:</strong> [Your Name]<br/>
<strong>Status:</strong> [Draft/In Review/Approved]<br/>
<strong>Last Updated:</strong> [Date]</p>

<h3>📋 Overview</h3>
<p><strong>Problem Statement:</strong><br/>
[Clear description of the problem we're solving]</p>

<p><strong>Proposed Solution:</strong><br/>
[High-level description of the solution]</p>

<h3>🎯 Goals & Success Metrics</h3>
<table>
<tr><th>Goal</th><th>Metric</th><th>Target</th></tr>
<tr><td>[Goal 1]</td><td>[Metric]</td><td>[Target]</td></tr>
<tr><td>[Goal 2]</td><td>[Metric]</td><td>[Target]</td></tr>
</table>

<h3>👥 User Stories</h3>
<p><strong>As a</strong> [user type], <strong>I want to</strong> [action], <strong>so that</strong> [benefit].</p>
<p><strong>As a</strong> [user type], <strong>I want to</strong> [action], <strong>so that</strong> [benefit].</p>

<h3>📐 Scope</h3>
<p><strong>In Scope:</strong></p>
<ul>
<li>[Feature/functionality]</li>
<li>[Feature/functionality]</li>
</ul>

<p><strong>Out of Scope:</strong></p>
<ul>
<li>[Feature/functionality] - [Reason]</li>
<li>[Feature/functionality] - [Reason]</li>
</ul>

<h3>🔧 Functional Requirements</h3>
<ol>
<li><strong>[Requirement 1]:</strong> [Detailed description]</li>
<li><strong>[Requirement 2]:</strong> [Detailed description]</li>
<li><strong>[Requirement 3]:</strong> [Detailed description]</li>
</ol>

<h3>🎨 Design</h3>
<p>[Link to Figma/design files]</p>

<h3>⚙️ Technical Considerations</h3>
<ul>
<li>[Technical constraint or consideration]</li>
<li>[API requirements]</li>
<li>[Data model changes]</li>
</ul>

<h3>🔒 Security & Privacy</h3>
<ul>
<li>[Security consideration]</li>
<li>[Privacy/compliance requirement]</li>
</ul>

<h3>📊 Analytics & Tracking</h3>
<ul>
<li>[Event to track]: [Purpose]</li>
<li>[Event to track]: [Purpose]</li>
</ul>

<h3>🧪 Testing Requirements</h3>
<ul>
<li>[Test scenario 1]</li>
<li>[Test scenario 2]</li>
</ul>

<h3>📅 Timeline</h3>
<table>
<tr><th>Phase</th><th>Duration</th><th>Dates</th></tr>
<tr><td>Design</td><td>[X] weeks</td><td>[Dates]</td></tr>
<tr><td>Development</td><td>[X] weeks</td><td>[Dates]</td></tr>
<tr><td>QA</td><td>[X] weeks</td><td>[Dates]</td></tr>
<tr><td>Launch</td><td>—</td><td>[Date]</td></tr>
</table>

<h3>🚨 Risks & Mitigations</h3>
<table>
<tr><th>Risk</th><th>Impact</th><th>Mitigation</th></tr>
<tr><td>[Risk]</td><td>High/Med/Low</td><td>[Mitigation]</td></tr>
</table>

<h3>📎 Appendix</h3>
<ul>
<li>[Link to research]</li>
<li>[Link to competitive analysis]</li>
<li>[Link to user feedback]</li>
</ul>`,
    category: "product",
  },
  {
    id: "beta-feedback-summary",
    name: "Beta Feedback Summary",
    description: "Summary of feedback from beta testers",
    subject: "[Feature Name] Beta Feedback Summary - [Date]",
    content: `<h2>Beta Feedback Summary</h2>
<p><strong>Feature:</strong> [Feature Name]<br/>
<strong>Beta Period:</strong> [Start Date] - [End Date]<br/>
<strong>Participants:</strong> [X] users<br/>
<strong>Author:</strong> [Your Name]</p>

<h3>📊 Overview</h3>
<table>
<tr><td><strong>Total Participants:</strong></td><td>[X]</td></tr>
<tr><td><strong>Active Users:</strong></td><td>[X] ([XX%])</td></tr>
<tr><td><strong>Feedback Submissions:</strong></td><td>[X]</td></tr>
<tr><td><strong>Avg. Satisfaction Score:</strong></td><td>[X]/5</td></tr>
<tr><td><strong>Would Recommend:</strong></td><td>[XX%]</td></tr>
</table>

<h3>🎯 Key Findings</h3>
<ol>
<li><strong>[Finding 1]:</strong> [Summary with supporting data]</li>
<li><strong>[Finding 2]:</strong> [Summary with supporting data]</li>
<li><strong>[Finding 3]:</strong> [Summary with supporting data]</li>
</ol>

<h3>💚 What Users Loved</h3>
<ul>
<li><strong>[Positive 1]:</strong> "[Direct quote]" — [X] mentions</li>
<li><strong>[Positive 2]:</strong> "[Direct quote]" — [X] mentions</li>
</ul>

<h3>🔴 Top Issues & Concerns</h3>
<table>
<tr><th>Issue</th><th>Frequency</th><th>Severity</th><th>Action</th></tr>
<tr><td>[Issue 1]</td><td>[X] users</td><td>High</td><td>[Fix before launch]</td></tr>
<tr><td>[Issue 2]</td><td>[X] users</td><td>Medium</td><td>[Fix in v2]</td></tr>
<tr><td>[Issue 3]</td><td>[X] users</td><td>Low</td><td>[Backlog]</td></tr>
</table>

<h3>💡 Feature Requests</h3>
<ul>
<li><strong>[Request 1]:</strong> [X] users — [Consideration for roadmap]</li>
<li><strong>[Request 2]:</strong> [X] users — [Consideration for roadmap]</li>
</ul>

<h3>📝 Notable Quotes</h3>
<blockquote>"[Powerful testimonial quote]" — [User type/segment]</blockquote>
<blockquote>"[Constructive criticism quote]" — [User type/segment]</blockquote>

<h3>✅ Recommended Actions Before Launch</h3>
<ol>
<li><strong>Must Fix:</strong> [Critical issue]</li>
<li><strong>Should Fix:</strong> [Important issue]</li>
<li><strong>Nice to Have:</strong> [Minor improvement]</li>
</ol>

<h3>📈 Launch Readiness</h3>
<p><strong>Recommendation:</strong> [Ready to launch / Needs more iteration / Major issues to address]</p>
<p><strong>Confidence Level:</strong> [High/Medium/Low]</p>

<h3>📎 Appendix</h3>
<ul>
<li>[Link to full survey responses]</li>
<li>[Link to user interview recordings]</li>
<li>[Link to analytics dashboard]</li>
</ul>`,
    category: "product",
  },
];

export const categoryLabels: Record<string, string> = {
  founders: "FOUNDERS",
  engineering: "ENGINEERING MANAGERS",
  product: "PRODUCT MANAGERS",
};
